import { forwardRef, type ReactNode, type MouseEvent } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { trackCtaClick, type CtaType } from "@/lib/ctaTracking";

interface CtaLinkProps extends Omit<LinkProps, "onClick"> {
  cta: CtaType;
  location: string;
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Drop-in replacement for <Link> that fires a CTA click event before
 * the navigation occurs. Tracking is fire-and-forget; navigation is
 * never delayed or blocked.
 */
export const CtaLink = forwardRef<HTMLAnchorElement, CtaLinkProps>(
  ({ cta, location, onClick, children, ...rest }, ref) => {
    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      // fire-and-forget
      void trackCtaClick(cta, location);
      onClick?.(e);
    };

    return (
      <Link ref={ref} onClick={handleClick} {...rest}>
        {children}
      </Link>
    );
  }
);

CtaLink.displayName = "CtaLink";
